<template>
  <div class="allocation-editor">
    <div v-for="row in rows" :key="row.uid" class="allocation">
      <div v-if="!fixedOrderId || hasLines" class="allocation-target">
        <CustomSelectField v-if="!fixedOrderId" :content=row.orderId />
        <CustomSelectField v-if="hasLines" :content=row.lineId />
      </div>
      <div class="allocation-value">
        <CustomSelectField class="method" :content=row.method />
        <div class="value">
          <CustomTextField :content=row.value />
          <span v-if="unitOf(row)" class="suffix">{{ unitOf(row) }}</span>
        </div>
        <span class="result" :title="'Montant hors taxes imputé'">= {{ formatAmount(htOf(row)) }} HT</span>
        <button type="button" class="button-icon-custom" title="Retirer" @click="removeRow(row)"><span class="fas fa-xmark"></span></button>
      </div>
    </div>

    <div class="actions">
      <button type="button" class="link-button" @click="addRow()">{{ fixedOrderId ? '+ Ajouter une part' : '+ Associer un dossier' }}</button>
      <button v-if="canSplitEvenly" type="button" class="link-button" title="Répartit 100 % de l'achat (ou de l'article) à parts égales entre les dossiers" @click="splitEvenly">Répartir à parts égales</button>
    </div>
    <p v-if="rows.length > 0" class="totals" :class="{error: overflow}">
      Ventilé : {{ formatAmount(allocatedHt) }} HT sur {{ formatAmount(totalHt) }} HT
    </p>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import CustomSelectField from "@/components/forms/customs/customSelectField/CustomSelectField.vue";
import {NotificationService} from "@vues/v-common";
import {ALLOCATION_METHOD_ENUM, formatAmount, round2} from "@/purchase/utils";
import {allocationHt, allocationValue, buildAllocationPayload, remainingOf} from "@/purchase/allocations";
library.add(faXmark);

const {AMOUNT, PERCENT, QUANTITY} = ALLOCATION_METHOD_ENUM;

// Ventilation d'un achat sur des dossiers : une part par dossier (et par article si l'achat en a), saisie en quantité, en pourcentage ou en montant HT.
// C'est la seule façon de rattacher un achat à un dossier : la logistique (quantités) et la marge (montants) lisent les mêmes parts.
// Props : orderValues (dossiers proposés), fixedOrderId (toutes les parts visent ce dossier), lines (articles de l'achat, s'il en a), totalHt.
// Méthodes : load(parts), reset(), addRow(prefill), getValue() (parts à envoyer, undefined si la saisie est invalide).
export default {
  name: 'AllocationEditor',
  props: ['orderValues', 'fixedOrderId', 'lines', 'totalHt'],
  components: {CustomTextField, CustomSelectField},
  data() {
    return {
      rows: [],
      uidCounter: 0,
    };
  },
  computed: {
    hasLines() {
      return (this.lines ?? []).length > 0;
    },
    lineValues() {
      return (this.lines ?? []).map(line => ({label: line.designation, value: line.id}));
    },
    // Clés proposées : la quantité n'a de sens que pour un article
    methodValues() {
      return (this.hasLines ? [QUANTITY, PERCENT, AMOUNT] : [AMOUNT, PERCENT]).map(({label, value}) => ({label, value}));
    },
    allocatedHt() {
      return round2(this.rows.reduce((sum, row) => sum + this.htOf(row), 0));
    },
    overflow() {
      return this.allocatedHt > (Number(this.totalHt) || 0) + 0.001;
    },
    // Des parts qui partagent la même base (le même article, ou l'achat entier) peuvent être réparties à parts égales
    canSplitEvenly() {
      return this.sharedBases().length > 0;
    },
  },
  watch: {
    // Les articles de l'achat changent pendant la saisie : les parts suivent (article supprimé, achat qui n'a plus d'articles...)
    lines() {
      this.rows.forEach(row => this.syncRow(row));
    },
    orderValues() {
      this.rows.forEach(row => { row.orderId.selectValues = this.orderValues ?? []; });
    },
  },
  methods: {
    formatAmount,
    toNumber(value) {
      const number = Number(String(value ?? '').replace(',', '.'));
      return isNaN(number) ? 0 : number;
    },
    isBlank(value) {
      return String(value ?? '').trim() === '';
    },
    lineOf(row) {
      return this.hasLines ? (this.lines ?? []).find(line => line.id === row.lineId.value) : undefined;
    },
    htOf(row) {
      return allocationHt({method: row.method.value, value: this.toNumber(row.value.value), line: this.lineOf(row), totalHt: Number(this.totalHt) || 0});
    },
    unitOf(row) {
      if (row.method.value === PERCENT.value) return '%';
      if (row.method.value === AMOUNT.value) return '€ HT';
      return this.lineOf(row)?.unit ?? '';
    },
    newRow({orderId = this.fixedOrderId, lineId, method, value = ''} = {}) {
      this.uidCounter++;
      const row = {
        uid: this.uidCounter,
        orderId: {value: orderId, type: 'select', selectValues: this.orderValues ?? []},
        lineId: {value: lineId, type: 'select', selectValues: this.lineValues},
        method: {value: method ?? (this.hasLines ? QUANTITY.value : AMOUNT.value), type: 'select', selectValues: this.methodValues},
        value: {value, type: 'number', step: 'any', placeholder: '0'},
      };
      this.syncRow(row);
      return row;
    },
    // Garde une part cohérente avec les articles de l'achat : un article supprimé laisse la part sans article (à choisir à nouveau)
    syncRow(row) {
      row.lineId.selectValues = this.lineValues;
      row.method.selectValues = this.methodValues;
      if (this.hasLines) {
        if (!this.lineValues.some(line => line.value === row.lineId.value)) row.lineId.value = undefined;
      } else {
        row.lineId.value = undefined;
        if (row.method.value === QUANTITY.value) row.method.value = AMOUNT.value;
      }
    },
    // Rows de la même base (même article, ou achat entier) que celle d'une part
    siblings(row) {
      return this.rows.filter(other => other !== row && other.lineId.value === row.lineId.value);
    },
    // Ce qui reste à ventiler est proposé comme valeur de la nouvelle part
    addRow(prefill = {}) {
      const row = this.newRow(prefill);
      if (this.isBlank(row.value.value)) {
        if (this.hasLines && !row.lineId.value) {
          // Premier article qui n'est pas encore entièrement ventilé
          const free = this.lineValues.find(line => this.remaining(row, line.value, true) > 0) ?? this.lineValues[0];
          row.lineId.value = free.value;
        }
        const remaining = this.remaining(row, row.lineId.value, row.method.value === QUANTITY.value);
        row.value.value = remaining > 0 ? remaining : '';
      }
      this.rows.push(row);
    },
    remaining(row, lineId, byQuantity) {
      const line = (this.lines ?? []).find(candidate => candidate.id === lineId);
      const others = this.rows
          .filter(other => other !== row && other.lineId.value === lineId)
          .map(other => ({method: other.method.value, value: this.toNumber(other.value.value)}));
      return remainingOf({rows: others, line, totalHt: Number(this.totalHt) || 0}, byQuantity);
    },
    removeRow(row) {
      this.rows.splice(this.rows.indexOf(row), 1);
    },
    // Bases (article ou achat entier) partagées par plusieurs parts
    sharedBases() {
      const counts = new Map();
      this.rows.forEach(row => counts.set(row.lineId.value ?? '', (counts.get(row.lineId.value ?? '') ?? 0) + 1));
      return [...counts.entries()].filter(([, count]) => count > 1).map(([base]) => base);
    },
    // 100 % de chaque base partagée, à parts égales (la dernière part prend le reliquat)
    splitEvenly() {
      for (const base of this.sharedBases()) {
        const group = this.rows.filter(row => (row.lineId.value ?? '') === base);
        const part = Math.floor(10000 / group.length) / 100;
        group.forEach((row, index) => {
          row.method.value = PERCENT.value;
          row.value.value = index === group.length - 1 ? round2(100 - part * (group.length - 1)) : part;
        });
      }
    },
    load(allocations = []) {
      this.rows = allocations.map(allocation => this.newRow({
        orderId: String(allocation.orderId), lineId: allocation.lineId, method: allocation.method ?? AMOUNT.value, value: allocationValue(allocation),
      }));
    },
    reset() {
      this.rows = [];
    },
    fail(input, message) {
      if (input) input.error = true;
      (new NotificationService).addNotification("danger", message);
    },
    // Parts à envoyer, sans les lignes laissées vides ; undefined si la saisie est invalide (message déjà affiché)
    getValue() {
      const rows = this.rows.filter(row => row.orderId.value || !this.isBlank(row.value.value));
      const targets = new Set();
      const allocations = [];

      // Sans article, une seule part sans valeur reçoit ce qui n'est pas encore ventilé sur les autres
      const blankRows = rows.filter(row => this.isBlank(row.value.value));
      const canFillRest = !this.hasLines && blankRows.length === 1;

      for (const row of rows) {
        if (!row.orderId.value) return this.fail(row.orderId, "Le dossier n'est pas renseigné");
        if (this.hasLines && !row.lineId.value) return this.fail(row.lineId, "L'article n'est pas renseigné");

        const target = `${row.orderId.value}|${row.lineId.value ?? ''}`;
        if (targets.has(target)) return this.fail(row.orderId, this.hasLines ? "Un dossier ne peut être associé qu'une fois par article" : "Un dossier ne peut être associé qu'une fois");
        targets.add(target);

        let method = row.method.value;
        let value = this.toNumber(row.value.value);
        if (this.isBlank(row.value.value) && canFillRest) {
          method = AMOUNT.value;
          value = this.remaining(row, undefined, false);
        }
        if (!(value > 0)) return this.fail(row.value, "La valeur d'une part n'est pas renseignée");

        allocations.push(buildAllocationPayload({orderId: row.orderId.value, lineId: row.lineId.value, method, value}));
      }
      return allocations;
    },
  },
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/purchase/style/section.styl';

.allocation-editor
  display flex
  flex-direction column

.allocation
  margin-bottom 8px
  padding 6px 10px 8px
  border 1px solid #e6eaf2
  border-radius 10px
  background #fbfcfe

.allocation-target
  display grid
  grid-template-columns repeat(auto-fit, minmax(150px, 1fr))
  gap 10px

.allocation-value
  display grid
  grid-template-columns 120px 1fr auto 28px
  align-items end
  gap 10px

  .value
    position relative

    .suffix
      position absolute
      right 14px
      top 50%
      transform translateY(-50%)
      font-size 0.8em
      color #9ca3af
      pointer-events none

  .result
    padding-bottom 10px
    font-size 0.85em
    white-space nowrap
    color #374151

.actions
  display flex
  flex-wrap wrap
  gap 4px 18px

@media (max-width 600px)
  .allocation-value
    grid-template-columns 1fr 1fr

    .result
      grid-column 1 / 3
</style>
